import { useEffect, useRef, useState } from "react";
import { ListChecks, Plus, Star, Trash2, GripVertical, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api/client";
import { todayIstIso } from "@/utils/date";

const fmt = (iso) => { const [, m, d] = iso.split("-"); return `${d}/${m}`; };
const err = (x) => toast.error(formatApiError(x?.response?.data?.detail) || "Something went wrong");

function TodoRow({ t, today, onToggle, onStar, onDelete, drag }) {
  return (
    <li draggable={!t.done} {...(t.done ? {} : drag)} data-testid={`todo-item-${t.id}`}
      className={`group flex items-center gap-2 rounded-md border border-white/10 bg-surface px-2 py-1.5 transition-colors ${t.done ? "opacity-60" : "hover:border-white/20"}`}>
      {!t.done && <GripVertical size={14} className="text-white/25 cursor-grab shrink-0" />}
      <button type="button" onClick={() => onToggle(t)} data-testid={`todo-toggle-${t.id}`} aria-label={t.done ? "Mark not done" : "Mark done"}
        className={`h-[18px] w-[18px] shrink-0 rounded border grid place-items-center transition-colors ${t.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-white/30 hover:border-emerald-500"}`}>
        {t.done && <Check size={12} strokeWidth={3} />}
      </button>
      <span className={`flex-1 min-w-0 text-sm break-words ${t.done ? "line-through text-white/50" : ""}`} data-testid={`todo-title-${t.id}`}>{t.title}</span>
      {!t.done && t.date < today && <span className="text-[10px] font-medium text-orange-600 dark:text-orange-400 shrink-0" data-testid={`todo-carried-${t.id}`}>from {fmt(t.date)}</span>}
      {!t.done && (
        <button type="button" onClick={() => onStar(t)} data-testid={`todo-star-${t.id}`} aria-label="Star"
          className={`shrink-0 transition-colors ${t.starred ? "text-amber-500" : "text-white/25 hover:text-amber-500 opacity-0 group-hover:opacity-100 focus:opacity-100"}`}>
          <Star size={14} fill={t.starred ? "currentColor" : "none"} />
        </button>
      )}
      <button type="button" onClick={() => onDelete(t)} data-testid={`todo-delete-${t.id}`} aria-label="Delete"
        className="shrink-0 text-white/25 hover:text-red-500 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"><Trash2 size={14} /></button>
    </li>
  );
}

const EVT = "lim:todos-changed";
export const notifyTodosChanged = () => window.dispatchEvent(new Event(EVT));

// Private daily to-do list; unfinished items roll over to the next day automatically.
export function MyTodos({ compact = false, onCount }) {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const dragId = useRef(null);
  const today = todayIstIso();
  const load = async () => { try { const { data } = await api.get("/my-day/todos"); setItems(data); } catch (x) { err(x); } };
  useEffect(() => { load(); window.addEventListener(EVT, load); return () => window.removeEventListener(EVT, load); }, []);
  useEffect(() => { onCount?.(items.filter((i) => !i.done).length); }, [items, onCount]);
  const changed = () => { window.removeEventListener(EVT, load); notifyTodosChanged(); window.addEventListener(EVT, load); };

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try { const { data } = await api.post("/my-day/todos", { title }); setItems((p) => { const open = p.filter((i) => !i.done); return [...open, data, ...p.filter((i) => i.done)]; }); setTitle(""); changed(); } catch (x) { err(x); }
  };
  const patch = async (t, body) => { try { await api.patch(`/my-day/todos/${t.id}`, body); load(); changed(); } catch (x) { err(x); } };
  const del = async (t) => { setItems((p) => p.filter((i) => i.id !== t.id)); try { await api.delete(`/my-day/todos/${t.id}`); changed(); } catch (x) { err(x); load(); } };

  const open = items.filter((i) => !i.done);
  const done = items.filter((i) => i.done);
  const dragProps = (t) => ({
    onDragStart: () => { dragId.current = t.id; },
    onDragOver: (e) => e.preventDefault(),
    onDrop: async () => {
      const from = dragId.current; dragId.current = null;
      if (!from || from === t.id) return;
      const ids = open.map((i) => i.id).filter((id) => id !== from);
      ids.splice(ids.indexOf(t.id), 0, from);
      setItems([...ids.map((id) => open.find((i) => i.id === id)), ...done]);
      try { await api.post("/my-day/todos/reorder", { ids }); changed(); } catch (x) { err(x); }
    },
  });

  return (
    <section className={compact ? "" : "mb-8"} data-testid={compact ? "widget-todos" : "my-day-todos"}>
      {!compact && <div className="flex items-center gap-2 mb-3">
        <ListChecks size={16} className="text-emerald-500" />
        <h2 className="text-base md:text-lg font-semibold">My to-dos</h2>
        <span className="text-xs text-white/50 font-mono" data-testid="todo-open-count">{open.length}</span>
        <span className="text-xs text-white/40 ml-1">Private to you · unfinished items roll over to tomorrow</span>
      </div>}
      <form onSubmit={add} className="flex gap-2 mb-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add something to do today…" maxLength={300} data-testid={compact ? "todos-input" : "todo-input"}
          className="flex-1 h-9 rounded-md border border-white/15 bg-surface px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
        <button type="submit" disabled={!title.trim()} data-testid={compact ? "todos-add-btn" : "todo-add-btn"}
          className="h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50 transition-colors"><Plus size={14} /> Add</button>
      </form>
      {open.length === 0 && done.length === 0 && <div className="text-xs text-white/40 py-2" data-testid="todo-empty">Nothing on your list yet.</div>}
      <ul className="space-y-1.5" data-testid="todo-open-list">
        {open.map((t) => <TodoRow key={t.id} t={t} today={today} drag={dragProps(t)} onToggle={() => patch(t, { done: true })} onStar={() => patch(t, { starred: !t.starred })} onDelete={del} />)}
      </ul>
      {done.length > 0 && (
        <div className="mt-3" data-testid="todo-done-section">
          <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1.5">Done today · {done.length}</div>
          <ul className="space-y-1.5">
            {done.map((t) => <TodoRow key={t.id} t={t} today={today} onToggle={() => patch(t, { done: false })} onDelete={del} />)}
          </ul>
        </div>
      )}
    </section>
  );
}
